import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Button,
    Box,
    Typography,
    Checkbox,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Alert,
    CircularProgress,
    Skeleton,
    Snackbar,
    List,
    ListItem,
    ListItemText,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Chip,
    Tab,
    Tabs,
    Card,
    CardMedia,
    CardContent,
    IconButton
} from '@mui/material';
import { ContentCopy as DuplicateIcon, CloudUpload as UploadIcon, Delete as DeleteIcon, Add as AddIcon, Inventory as InventoryIcon, Search as SearchIcon, FilterList as FilterListIcon, Close as CloseIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import InputAdornment from '@mui/material/InputAdornment';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// TabPanel component for the duplicate dialog tabs
function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
        </div>
    );
}

const Products = ({ api, isAuthenticated }) => {
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);
    const [stockQuantity, setStockQuantity] = useState('');
    const [productToEdit, setProductToEdit] = useState(null);
    const [productToDuplicate, setProductToDuplicate] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [duplicating, setDuplicating] = useState(false);
    const [filter, setFilter] = useState('all');
    const [activeTab, setActiveTab] = useState(0);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');

    const showSnackbar = (message, severity = 'success') =>
        setSnackbar({ open: true, message, severity });

    const [updatedProductData, setUpdatedProductData] = useState({
        name: '',
        description: '',
        price: '',
        costPrice: '',
        stock: '',
        unlimited: false,
        mainImage: '',
        galleryImages: []
    });

    // Duplicate product state
    const [duplicateData, setDuplicateData] = useState({
        newName: '',
        newSku: '',
        mainImage: '',
        galleryImages: [],
        originalProduct: null
    });

    useEffect(() => {
        if (isAuthenticated) {
            getProducts();
        }
    }, [isAuthenticated]);

    const getProducts = () => {
        setLoading(true);
        api.get('/api/products')
            .then(response => {
                setProducts(response.data.items || []);
                setLoading(false);
            })
            .catch(error => {
                setError('Failed to fetch products');
                setLoading(false);
            });
    };

    const handleSelectProduct = (productId) => {
        setSelectedProducts(prev =>
            prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]
        );
    };

    const handleSelectAll = () => {
        if (selectedProducts.length === filteredProducts.length) {
            setSelectedProducts([]);
        } else {
            setSelectedProducts(filteredProducts.map(product => product.id));
        }
    };

    const deleteProduct = () => {
        if (selectedProducts.length === 0) {
            showSnackbar('Please select products first', 'warning');
            return;
        }
        setLoading(true);
        Promise.all(selectedProducts.map(productId =>
            api.delete(`/api/products/${productId}`)
        )).then(() => {
            getProducts();
            setSelectedProducts([]);
            setLoading(false);
            showSnackbar('Products deleted successfully');
        }).catch(error => {
            setError('Failed to delete products');
            setLoading(false);
        });
    };

    const handleUpdateStock = () => {
        const quantity = parseInt(stockQuantity);
        if (isNaN(quantity) || quantity < 0) {
            setError('Please enter a valid quantity.');
            return;
        }
        setUpdating(true);
        Promise.all(selectedProducts.map(productId =>
            api.put(`/api/products/${productId}/stock`, { stock: quantity })
        )).then(() => {
            setUpdating(false);
            showSnackbar('Stock updated successfully');
            setOpenDialog(false);
            setStockQuantity('');
            // getProducts();
            setSelectedProducts([]);
        }).catch(error => {
            setError('Failed to update stock');
            setUpdating(false);
        });
    };

    const handleEditProduct = async (product) => {
        try {
            const response = await api.get(`/api/product/${product.id}`);
            const productData = response.data;
            setUpdatedProductData({
                name: productData.name,
                description: productData.description || '',
                price: productData.price || 0,
                stock: productData.quantity || 0,
                unlimited: Boolean(productData.unlimited),
                costPrice: productData.costPrice || 0,
                mainImage: productData.originalImageUrl || '',
                galleryImages: productData.galleryImages ? productData.galleryImages.map(img => img.url) : []
            });
            setProductToEdit(productData);
            setOpenEditDialog(true);
        } catch (error) {
            setError('Failed to load product details');
            console.error(error);
        }
    };

    const handleSaveProduct = () => {
        const { name, description, price, stock, unlimited, costPrice, mainImage, galleryImages } = updatedProductData;
        const updatedProduct = {
            name,
            description,
            price: parseFloat(price),
            costPrice: parseFloat(costPrice),
            stock: parseInt(stock),
            unlimited: Boolean(unlimited),
            mainImage: mainImage || undefined,
            galleryImages: galleryImages && galleryImages.length > 0 ? galleryImages : undefined
        };

        api.put(`/api/product/${productToEdit.id}`, updatedProduct)
            .then(() => {
                setOpenEditDialog(false);
                getProducts();
                showSnackbar('Product updated successfully!');
            })
            .catch(error => {
                setError('Failed to update product');
                console.error(error);
            });
    };

    // Handle duplicate product
    const handleDuplicateClick = (product) => {
        setProductToDuplicate(product);

        // Get original gallery image URLs
        const originalGalleryUrls = product.galleryImages?.map(img => img.url) || [];

        setDuplicateData({
            newName: `${product.name} (Copy)`,
            newSku: product.sku ? `${product.sku}-copy` : '',
            mainImage: product.originalImageUrl || '',
            galleryImages: [...originalGalleryUrls],
            originalProduct: product
        });
        setActiveTab(0);
        setOpenDuplicateDialog(true);
    };

    const handleDuplicateFieldChange = (field, value) => {
        setDuplicateData(prev => ({ ...prev, [field]: value }));
    };

    const handleAddGalleryImage = (url) => {
        setDuplicateData(prev => ({
            ...prev,
            galleryImages: [...prev.galleryImages, url]
        }));
    };

    const handleRemoveGalleryImage = (index) => {
        setDuplicateData(prev => ({
            ...prev,
            galleryImages: prev.galleryImages.filter((_, i) => i !== index)
        }));
    };

    const handleAddGalleryImageEdit = (url) => {
        setUpdatedProductData(prev => ({
            ...prev,
            galleryImages: [...prev.galleryImages, url]
        }));
    };

    const handleRemoveGalleryImageEdit = (index) => {
        setUpdatedProductData(prev => ({
            ...prev,
            galleryImages: prev.galleryImages.filter((_, i) => i !== index)
        }));
    };

    const handleImageUploadEdit = async (event, type) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        setUploadingImage(true);

        try {
            const response = await api.post('/api/upload-image', formData);
            const uploadedImageUrl = response.data.imageUrl;

            if (type === 'main') {
                setUpdatedProductData(prev => ({ ...prev, mainImage: uploadedImageUrl }));
            } else if (type === 'gallery') {
                handleAddGalleryImageEdit(uploadedImageUrl);
            }
            showSnackbar('Image uploaded successfully!');
        } catch (error) {
            console.error('Error uploading image:', error);
            showSnackbar('Failed to upload image', 'error');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleImageUpload = async (event, type) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        setUploadingImage(true);

        try {
            const response = await api.post('/api/upload-image', formData);
            const uploadedImageUrl = response.data.imageUrl;

            if (type === 'main') {
                setDuplicateData(prev => ({ ...prev, mainImage: uploadedImageUrl }));
            } else if (type === 'gallery') {
                handleAddGalleryImage(uploadedImageUrl);
            }
            showSnackbar('Image uploaded successfully!');
        } catch (error) {
            console.error('Error uploading image:', error);
            showSnackbar('Failed to upload image', 'error');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleConfirmDuplicate = async () => {
        if (!duplicateData.newName.trim()) {
            showSnackbar('Please enter a product name', 'warning');
            return;
        }

        setDuplicating(true);
        try {
            const response = await api.post(`/api/products/${productToDuplicate.id}/duplicate`, {
                newName: duplicateData.newName,
                newSku: duplicateData.newSku,
                newMainImage: duplicateData.mainImage,
                newGalleryImages: duplicateData.galleryImages
            });

            if (response.data.success) {
                showSnackbar('Product duplicated successfully!');
                setOpenDuplicateDialog(false);
                getProducts();
            } else {
                throw new Error('Duplicate failed');
            }
        } catch (error) {
            console.error('Error duplicating product:', error);
            showSnackbar('Failed to duplicate product: ' + (error.response?.data?.error || error.message), 'error');
        } finally {
            setDuplicating(false);
        }
    };

    const handleStockChange = async (productId, newStock) => {
        const quantity = parseInt(newStock);
        if (!isNaN(quantity) && quantity >= 0) {
            try {
                await api.put(`/api/products/${productId}/stock`, { stock: quantity });
                showSnackbar('Stock updated successfully');
                // getProducts();
            } catch (error) {
                console.error('Error updating stock:', error);
                showSnackbar('Failed to update stock', 'error');
            }
        }
    };

    const renderLoader = () => (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
                <TableBody>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <TableRow key={i}>
                            <TableCell padding="checkbox"><Skeleton variant="rectangular" width={18} height={18} /></TableCell>
                            <TableCell><Skeleton width={40} /></TableCell>
                            <TableCell><Skeleton variant="rectangular" width={50} height={50} /></TableCell>
                            <TableCell><Skeleton width="80%" /></TableCell>
                            <TableCell><Skeleton width={60} /></TableCell>
                            <TableCell><Skeleton width={80} /></TableCell>
                            <TableCell><Skeleton width={120} /></TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const clearFilters = () => {
        setSearchQuery('');
        setFilter('all');
        setMinPrice('');
        setMaxPrice('');
        setPage(0);
    };

    const hasActiveFilters = searchQuery !== '' || filter !== 'all' || minPrice !== '' || maxPrice !== '';

    const filteredProducts = products.filter(product => {
        if (filter === 'inStock' && product.inStock !== true) return false;
        if (filter === 'outOfStock' && product.inStock !== false) return false;
        if (searchQuery && !product.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
        if (minPrice !== '' && product.price < parseFloat(minPrice)) return false;
        if (maxPrice !== '' && product.price > parseFloat(maxPrice)) return false;
        return true;
    });

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h4" sx={{ fontSize: '1.5rem' }}>
                    Products
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {hasActiveFilters && (
                        <Chip
                            label={`${filteredProducts.length} result${filteredProducts.length !== 1 ? 's' : ''}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    )}
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={getProducts}
                        disabled={loading}
                    >
                        Sync Products
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by product name..."
                        value={searchQuery}
                        onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => { setSearchQuery(''); setPage(0); }}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Stock</InputLabel>
                        <Select
                            value={filter}
                            onChange={(e) => { setFilter(e.target.value); setPage(0); }}
                            label="Stock"
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="inStock">In Stock</MenuItem>
                            <MenuItem value="outOfStock">Out of Stock</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Min Price"
                        type="number"
                        value={minPrice}
                        onChange={(e) => { setMinPrice(e.target.value); setPage(0); }}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                    />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Max Price"
                        type="number"
                        value={maxPrice}
                        onChange={(e) => { setMaxPrice(e.target.value); setPage(0); }}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                    />
                </Grid>
                {hasActiveFilters && (
                    <Grid item xs={12} sm={12} md={2}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={clearFilters}
                            startIcon={<FilterListIcon />}
                            fullWidth
                        >
                            Clear Filters
                        </Button>
                    </Grid>
                )}
            </Grid>

            {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

            {loading && renderLoader()}

            {!loading && (
                <>
                    {filteredProducts.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <InventoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                No products found
                            </Typography>
                            <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
                                {hasActiveFilters ? 'No products match your search or filters.' : 'Add new products to get started.'}
                            </Typography>
                            {hasActiveFilters && (
                                <Button variant="outlined" onClick={clearFilters}>
                                    Clear filters
                                </Button>
                            )}
                        </Box>
                    ) : (
                    <TableContainer component={Paper} sx={{ mt: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    <TableCell padding="none" align="center">ID</TableCell>
                                    <TableCell padding="none">Image</TableCell>
                                    <TableCell padding="none">Name</TableCell>
                                    <TableCell padding="none">Price</TableCell>
                                    <TableCell padding="none">Stock</TableCell>
                                    <TableCell padding="none">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(product => (
                                    <TableRow
                                        key={product.id}
                                        sx={{
                                            backgroundColor: product.inStock === false ? '#FEF2F2' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: product.inStock === false ? '#FEE2E2' : 'action.hover',
                                            },
                                        }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedProducts.includes(product.id)}
                                                onChange={() => handleSelectProduct(product.id)}
                                            />
                                        </TableCell>
                                        <TableCell padding="none" align="center">
                                            {product.id}
                                            {product.inStock === false && (
                                                <Chip
                                                    label="Out of Stock"
                                                    color="error"
                                                    size="small"
                                                    sx={{ ml: 1, mt: 0.5 }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell padding="none">
                                            <img
                                                src={product.smallThumbnailUrl || 'https://placehold.co/50'}
                                                alt={product.name}
                                                style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: 4 }}
                                            />
                                        </TableCell>
                                        <TableCell padding="none">
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                {product.name}
                                            </Box>
                                        </TableCell>
                                        <TableCell padding="none">{product.defaultDisplayedPriceFormatted}</TableCell>
                                        <TableCell padding="none">
                                            <TextField
                                                type="number"
                                                size="small"
                                                defaultValue={product.quantity}
                                                onBlur={(e) => handleStockChange(product.id, e.target.value)}
                                                sx={{ maxWidth: 100 }}
                                                inputProps={{ min: 0 }}
                                            />
                                        </TableCell>
                                        <TableCell padding="none">
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                onClick={() => handleEditProduct(product)}
                                                size="small"
                                                sx={{ mr: 1 }}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                onClick={() => handleDuplicateClick(product)}
                                                size="small"
                                                startIcon={<DuplicateIcon />}
                                            >
                                                Duplicate
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={filteredProducts.length}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[10, 25, 50]}
                        />
                    </TableContainer>
                    )}

                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setOpenDialog(true)}
                            sx={{ mr: 2 }}
                            disabled={selectedProducts.length === 0}
                        >
                            Update Stock
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={deleteProduct}
                            disabled={selectedProducts.length === 0}
                        >
                            Delete Selected
                        </Button>
                    </Box>
                </>
            )}

            {/* Update Stock Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Update Stock for Selected Products</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Stock Quantity"
                        variant="outlined"
                        fullWidth
                        size="small"
                        type="number"
                        value={stockQuantity}
                        onChange={(e) => setStockQuantity(e.target.value)}
                        sx={{ mt: 1 }}
                        inputProps={{ min: 0 }}
                    />
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Selected Products:</Typography>
                    <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                        {selectedProducts.map(productId => {
                            const product = products.find(p => p.id === productId);
                            return (
                                <ListItem key={productId}>
                                    <ListItemText
                                        primary={product?.name}
                                        secondary={`Current Stock: ${product?.quantity}`}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button onClick={handleUpdateStock} variant="contained" disabled={updating}>
                        {updating ? <CircularProgress size={24} /> : 'Update Stock'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Product Dialog */}
            <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Edit Product</DialogTitle>
                <DialogContent>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, mt: 1 }}>
                        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                            <Tab label="Basic Info" />
                            <Tab label="Images" />
                        </Tabs>
                    </Box>

                    <TabPanel value={activeTab} index={0}>
                        <TextField
                            label="Product Name"
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={updatedProductData.name}
                            onChange={(e) => setUpdatedProductData({ ...updatedProductData, name: e.target.value })}
                            sx={{ mb: 2 }}
                        />
                        <ReactQuill
                            value={updatedProductData.description}
                            onChange={(value) => setUpdatedProductData({ ...updatedProductData, description: value })}
                            placeholder="Enter product description"
                            theme="snow"
                            style={{ marginBottom: '16px', height: '200px' }}
                        />
                        <Box sx={{ display: 'flex', gap: 2, mt: 6, flexWrap: 'wrap' }}>
                            <TextField
                                label="Price"
                                variant="outlined"
                                fullWidth
                                size="small"
                                type="number"
                                value={updatedProductData.price}
                                onChange={(e) => setUpdatedProductData({ ...updatedProductData, price: e.target.value })}
                            />
                            <TextField
                                label="Cost Price"
                                variant="outlined"
                                fullWidth
                                size="small"
                                type="number"
                                value={updatedProductData.costPrice}
                                onChange={(e) => setUpdatedProductData({ ...updatedProductData, costPrice: e.target.value })}
                            />
                            <TextField
                                label="Stock Quantity"
                                variant="outlined"
                                fullWidth
                                size="small"
                                type="number"
                                value={updatedProductData.stock}
                                onChange={(e) => setUpdatedProductData({ ...updatedProductData, stock: e.target.value })}
                                inputProps={{ min: 0 }}
                            />
                        </Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={updatedProductData.unlimited}
                                    onChange={(e) => setUpdatedProductData({ ...updatedProductData, unlimited: e.target.checked })}
                                />
                            }
                            label="Unlimited stock"
                            sx={{ mt: 2 }}
                        />
                    </TabPanel>

                    <TabPanel value={activeTab} index={1}>
                        <Typography variant="subtitle1" gutterBottom>Main Image</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            Upload a new main image for this product
                        </Typography>

                        <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                {updatedProductData.mainImage && (
                                    <Box sx={{ position: 'relative' }}>
                                        <img
                                            src={updatedProductData.mainImage}
                                            alt="Main"
                                            style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '8px' }}
                                        />
                                        <IconButton
                                            size="small"
                                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white' }}
                                            onClick={() => setUpdatedProductData({ ...updatedProductData, mainImage: '' })}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )}
                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<UploadIcon />}
                                    disabled={uploadingImage}
                                >
                                    {uploadingImage ? 'Uploading...' : 'Upload Main Image'}
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={(e) => handleImageUploadEdit(e, 'main')}
                                    />
                                </Button>
                            </Box>
                        </Card>

                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>Gallery Images</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            Add or remove gallery images for this product
                        </Typography>

                        <Card variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                                {updatedProductData.galleryImages.map((img, index) => (
                                    <Box key={index} sx={{ position: 'relative' }}>
                                        <img
                                            src={img}
                                            alt={`Gallery ${index + 1}`}
                                            style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '8px' }}
                                        />
                                        <IconButton
                                            size="small"
                                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white' }}
                                            onClick={() => handleRemoveGalleryImageEdit(index)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                                {updatedProductData.galleryImages.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{ width: '100%', textAlign: 'center', py: 2 }}>
                                        No gallery images selected
                                    </Typography>
                                )}
                            </Box>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<AddIcon />}
                                disabled={uploadingImage}
                            >
                                {uploadingImage ? 'Uploading...' : 'Add Gallery Image'}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={(e) => handleImageUploadEdit(e, 'gallery')}
                                />
                            </Button>
                        </Card>
                    </TabPanel>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
                    <Button onClick={handleSaveProduct} variant="contained" color="primary">
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Duplicate Product Dialog */}
            <Dialog open={openDuplicateDialog} onClose={() => setOpenDuplicateDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Duplicate Product: {productToDuplicate?.name}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                            <Tab label="Basic Info" />
                            <Tab label="Images" />
                        </Tabs>
                    </Box>

                    <TabPanel value={activeTab} index={0}>
                        <TextField
                            label="New Product Name"
                            variant="outlined"
                            fullWidth
                            value={duplicateData.newName}
                            onChange={(e) => handleDuplicateFieldChange('newName', e.target.value)}
                            sx={{ mb: 2 }}
                            required
                        />
                        <TextField
                            label="New SKU (Optional)"
                            variant="outlined"
                            fullWidth
                            value={duplicateData.newSku}
                            onChange={(e) => handleDuplicateFieldChange('newSku', e.target.value)}
                            helperText="Leave empty to auto-generate"
                        />
                    </TabPanel>

                    <TabPanel value={activeTab} index={1}>
                        <Typography variant="subtitle1" gutterBottom>Main Image</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            Upload a new main image or leave as is to use the original product's image
                        </Typography>

                        <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                {duplicateData.mainImage && (
                                    <Box sx={{ position: 'relative' }}>
                                        <img
                                            src={duplicateData.mainImage}
                                            alt="Main"
                                            style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '8px' }}
                                        />
                                        <IconButton
                                            size="small"
                                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white' }}
                                            onClick={() => handleDuplicateFieldChange('mainImage', '')}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )}
                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<UploadIcon />}
                                    disabled={uploadingImage}
                                >
                                    {uploadingImage ? 'Uploading...' : 'Upload Main Image'}
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={(e) => handleImageUpload(e, 'main')}
                                    />
                                </Button>
                            </Box>
                        </Card>

                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>Gallery Images</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            Upload new gallery images or leave as is to use the original product's gallery images<br/>
                            You can also remove individual images by clicking the delete icon
                        </Typography>

                        <Card variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                                {duplicateData.galleryImages.map((img, index) => (
                                    <Box key={index} sx={{ position: 'relative' }}>
                                        <img
                                            src={img}
                                            alt={`Gallery ${index + 1}`}
                                            style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '8px' }}
                                        />
                                        <IconButton
                                            size="small"
                                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white' }}
                                            onClick={() => handleRemoveGalleryImage(index)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                                {duplicateData.galleryImages.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{ width: '100%', textAlign: 'center', py: 2 }}>
                                        No gallery images selected
                                    </Typography>
                                )}
                            </Box>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<AddIcon />}
                                disabled={uploadingImage}
                            >
                                {uploadingImage ? 'Uploading...' : 'Add Gallery Image'}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={(e) => handleImageUpload(e, 'gallery')}
                                />
                            </Button>
                        </Card>
                    </TabPanel>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDuplicateDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleConfirmDuplicate}
                        variant="contained"
                        color="primary"
                        disabled={duplicating || !duplicateData.newName.trim()}
                    >
                        {duplicating ? <CircularProgress size={24} /> : 'Duplicate Product'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Products;